import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Button, Container } from '@mantine/core';
import {
    IconAlertTriangle,
    IconDownload,
    IconUpload,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client';

import { DragDrop } from '../../../components/file-uploads/DragDrop';
import { DashboardHeader } from '../../../components/common';
import { ImportType } from '../../../types/import';
import { GET_TEMPLATE_FILE } from '../../../apollo/queries/getTemplateFile';
import { useAuth } from '../../../context/AuthProvider';
import { checkUserRole } from '../../../utility/userRoles';
import { RESTRICTED_SKU_UPLOAD_ROLES } from '../../../constants/restrictedSkuUploadAccess';
import RestrictedSkuUploadHistoryTable from './RestrictedSkuUploadHistoryTable';
import { showErrorNotification } from '../../../notifications';
import { getErrorMessage } from '../../../helpers/getErrorMessage';

function RestrictedSkuUploadPage() {
    const { authUser } = useAuth();
    const canAccessUpload = checkUserRole(
        authUser,
        RESTRICTED_SKU_UPLOAD_ROLES,
    );

    const [refreshKey, setRefreshKey] = useState(0);
    const [downloadTemplate, setDownloadTemplate] = useState(false);

    const { data, error } = useQuery(GET_TEMPLATE_FILE, {
        variables: { inputType: ImportType.RestrictedSkuList },
        fetchPolicy: 'network-only',
        skip: !downloadTemplate,
    });

    useEffect(() => {
        if (!downloadTemplate) {
            return;
        }

        if (error) {
            showErrorNotification(getErrorMessage(error));
            setDownloadTemplate(false);
            return;
        }

        if (data?.getManualImportTemplateFile) {
            const { buffer, fileName } = data.getManualImportTemplateFile;
            try {
                const byteCharacters = atob(buffer);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i += 1) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } finally {
                setDownloadTemplate(false);
            }
        }
    }, [data, error, downloadTemplate]);

    if (!canAccessUpload) {
        return <Navigate to="/price-change" replace />;
    }

    return (
        <div className="relative pb-10">
            <DashboardHeader
                title="Price Change - Restricted SKU upload"
                hasBreadcrumb={false}
                hasDate
            />
            <div className="px-11 w-full mb-8">
                <Container size="xl" className="rounded-lg p-4 bg-white">
                    <div className="flex items-center w-full mb-4">
                        <div className="flex items-center gap-2">
                            <IconUpload size={24} />
                            <span className="text-lg font-medium whitespace-nowrap">
                                Restricted SKU / store list upload
                            </span>
                        </div>
                        <div className="ml-auto flex items-center">
                            <Button
                                variant="subtle"
                                leftSection={<IconDownload size={16} />}
                                onClick={() => setDownloadTemplate(true)}
                            >
                                Download Template
                            </Button>
                        </div>
                    </div>

                    <Alert
                        icon={<IconAlertTriangle size={16} />}
                        color="orange"
                        variant="light"
                        className="mb-6"
                    >
                        This upload fully replaces the current restricted SKU
                        list. Any SKU/store/restriction combination not included
                        in this file will be removed. Store-level price change
                        requests for a matching SKU + store combination will be
                        automatically declined using the reason provided - both
                        new ones and any that are still open and have not yet
                        gone to SAP. Zone requests stay open and simply skip the
                        restricted stores.
                    </Alert>

                    <p className="text-sm text-gray-500 mb-6">
                        Columns required: SKU / Article Number, Store No.,
                        Restriction Type (No Increase, No Decrease or No PCR),
                        Decline Reason.
                    </p>

                    <DragDrop
                        importType={ImportType.RestrictedSkuList}
                        title="Click or drag and drop your file here"
                        description="Supported formats: XLSX, XLS, CSV"
                        onUploadComplete={() =>
                            setRefreshKey(current => current + 1)
                        }
                    />
                </Container>

                <RestrictedSkuUploadHistoryTable refreshKey={refreshKey} />
            </div>
        </div>
    );
}

export default RestrictedSkuUploadPage;
